import { Fragment, useEffect, useState } from "react";
import { AddIcon, DeleteIcon, Icon } from "@chakra-ui/icons";
import { AO_NOTES_STRUCTURE } from "@/utils/profile";
import { useEditProfileContext } from "./edit/shared";
import { Box, Button, Grid, Heading, IconButton, Input, Text, Textarea } from "@chakra-ui/react";
import { FaTrash } from "react-icons/fa";
import { useProfile } from "@/contexts/ProfileContext";

export const ViewableAONotes = () => {
  const {
    profile: { ao_notes: evals },
  } = useProfile();
  return (
    <Box p={4} bg="whiteAlpha.700">
      {AO_NOTES_STRUCTURE.map((structure) => {
        const evaluations = evals.filter((evaluation) => evaluation.name === structure.name);

        return evaluations.map((evaluation, evalIndex) => {
          const { name, metrics, overall } = evaluation;

          return (
            <Fragment key={evalIndex}>
              <Text fontWeight="bold" fontSize="2xl" borderBottom="1px dashed red">
                {name}
                {structure.repeatable && " #" + (evalIndex + 1)}
              </Text>
              <Text fontSize="xl">
                <b>Notes &amp; Comments:&nbsp;</b>
                {overall}
              </Text>
              {metrics.map((metric, metricIndex) => {
                return (
                  <Text key={metricIndex} fontSize="xl">
                    <b>{metric.name}:&nbsp;</b>
                    {metric.value}
                  </Text>
                );
              })}
            </Fragment>
          );
        });
      })}
    </Box>
  );
};

export const EditableAONotes = () => {
  const { initialProfileData, update } = useEditProfileContext();
  const [evals, setEvals] = useState(initialProfileData.ao_notes ?? []);

  useEffect(() => update("ao_notes", evals), [evals]);

  const addStructure = (name) =>
    setEvals([
      ...evals,
      {
        name: name,
        metrics: [],
        overall: "",
      },
    ]);

  return AO_NOTES_STRUCTURE.map((structure, structureIndex) => {
    const evaluations = evals.filter((evaluation) => evaluation.name === structure.name);
    const hasEvals = evaluations.length > 0;

    return (
      <Box key={structureIndex}>
        {(!hasEvals || structure.repeatable) && (
          <Button
            mt={4}
            colorScheme="green"
            leftIcon={<AddIcon />}
            onClick={() => addStructure(structure.name)}
          >
            Add {structure.name}
          </Button>
        )}
        {hasEvals &&
          evaluations.map((evaluation, index) => {
            const { name, metrics, overall } = evaluation;
            const realIndex = evals.indexOf(evaluation);

            return (
              <Box key={evaluation.toString() + index.toString()} mt={4}>
                <Text fontSize="2xl" fontWeight="bold">
                  {name}
                  {structure.repeatable && " #" + (index + 1)}
                  <Button
                    variant="ghost"
                    colorScheme="red"
                    size="sm"
                    leftIcon={<Icon as={FaTrash} />}
                    float="right"
                    onClick={() => {
                      const newEvals = [...evals];
                      newEvals.splice(realIndex, 1);
                      setEvals(newEvals);
                    }}
                  >
                    Remove {name}
                  </Button>
                </Text>
                <Textarea
                  value={overall}
                  onChange={(e) => {
                    const newEvals = [...evals];
                    newEvals[realIndex] = { ...evaluation, overall: e.target.value };
                    setEvals(newEvals);
                  }}
                  rows={2}
                  placeholder="Comments & Notes"
                />
                <Text mt={4}>
                  Did you receive any quantitative evaluations? If so, add up to 10 metrics.
                  <Button
                    isDisabled={metrics.length >= 10}
                    float="right"
                    colorScheme="green"
                    variant="ghost"
                    size="sm"
                    leftIcon={<AddIcon />}
                    onClick={() => {
                      const newEvals = [...evals];
                      newEvals[realIndex] = {
                        ...evaluation,
                        metrics: [
                          ...evaluation.metrics,
                          {
                            name: "",
                            value: "",
                          },
                        ],
                      };
                      setEvals(newEvals);
                    }}
                  >
                    Add Metric
                  </Button>
                </Text>
                <Text fontStyle="italic">Example: High School Rigor = 2</Text>
                <Grid templateColumns="5fr 1fr min-content" rowGap={1} mt={4} columnGap={2}>
                  {metrics.map((metric, mi) => (
                    <>
                      <Input
                        value={metric.name}
                        onChange={(e) => {
                          const copy = [...metrics];
                          copy[mi].name = e.target.value;
                          const copyEvals = [...evals];
                          copyEvals[realIndex] = { ...evaluation, metrics: copy };
                          setEvals(copyEvals);
                        }}
                        placeholder="Intellectual Vitality"
                      />
                      <Input
                        value={metric.value}
                        onChange={(e) => {
                          const copy = [...metrics];
                          copy[mi].value = e.target.value;
                          const copyEvals = [...evals];
                          copyEvals[realIndex] = { ...evaluation, metrics: copy };
                          setEvals(copyEvals);
                        }}
                        placeholder="2"
                      />
                      <IconButton
                        colorScheme="red"
                        variant="ghost"
                        key={`delete-${mi}`}
                        icon={<DeleteIcon />}
                        onClick={() => {
                          const copy = [...metrics];
                          copy.splice(mi, 1);
                          const copyEvals = [...evals];
                          copyEvals[realIndex] = { ...evaluation, metrics: copy };
                          setEvals(copyEvals);
                        }}
                      />
                    </>
                  ))}
                </Grid>
              </Box>
            );
          })}
      </Box>
    );
  });
};
